import classNames from "classnames";
import { ComponentProps, ForwardedRef, forwardRef, ReactNode } from "react";
import Icon from "../../icons/Icon/Icon";
import Text from "../../type/Text/Text";
import type {
  ButtonTextSizeOptions,
  DropdownOptions,
  DropdownProps,
} from "./Dropdown";
import styles from "./Dropdown.module.scss";

const buttonTextSizeClasses: Record<
  ButtonTextSizeOptions,
  { tw: string; text: ComponentProps<typeof Text>["variant"] }
> = {
  small: { tw: "text-bs", text: "sb_t-14-500" },
  xs: { tw: "text-xs", text: "sb_t-12-500" },
  base: { tw: "text-bn", text: "sb_t-14-500" },
};

type DropdownTargetProps<OptionIDType, OptionMetadata extends {} | void> = Pick<
  DropdownProps<OptionIDType, OptionMetadata>,
  | "disabled"
  | "variant"
  | "status"
  | "tint"
  | "positionOptionsRelative"
  | "dataTestId"
  | "placeholder"
  | "buttonTextSize"
> & {
  readonly dropdownText: ReactNode;
  readonly dropdownValue?: DropdownOptions<OptionIDType, OptionMetadata>;
  readonly menuOpened: boolean;
} & ComponentProps<"button">;

const DropdownTarget = <
  OptionIDType extends string | number = string,
  OptionMetadata extends {} | void = void
>(
  {
    disabled,
    dropdownText,
    variant = "normal",
    status = "default",
    tint,
    positionOptionsRelative,
    dropdownValue,
    dataTestId,
    placeholder,
    buttonTextSize = "base",
    menuOpened,
    ...buttonProps
  }: DropdownTargetProps<OptionIDType, OptionMetadata>,
  ref: ForwardedRef<HTMLButtonElement>
) => {
  const isBrandTint = tint === "brand";
  const brandTintColor = isBrandTint ? "white" : undefined;

  return (
    <button
      {...buttonProps}
      data-testid={dataTestId ?? "dropdown-container"}
      className={classNames(
        "flex w-full items-center justify-between px-[18px] py-2 outline-none transition-all focus:outline-none",
        "group",
        {
          "text-straps-primary placeholder:text-straps-tertiary hover:shadow-lg":
            !isBrandTint,
          "text-white placeholder:text-white/70 hover:bg-straps-brand-hover":
            isBrandTint,
          "pointer-events-none cursor-not-allowed opacity-25": disabled,
          "h-[70px]": variant === "largeSearch",
          "h-[34px] rounded-full": variant !== "largeSearch",
          "bg-pure-white":
            status === "default" && tint !== "darker" && !isBrandTint,
          "bg-straps-brand": status === "default" && isBrandTint,
          "bg-straps-positive-bg": status === "positive",
          "bg-straps-negative-bg": status === "negative",
          "bg-straps-warning-bg": status === "warning",
          "bg-straps-body": tint === "darker",
          "mb-0.5 ": positionOptionsRelative,
        }
      )}
      ref={ref}
    >
      <div
        className={classNames(
          "flex w-full max-w-[90%] flex-row items-center gap-x-3",
          styles.dropdownTextContainer
        )}
      >
        {dropdownValue && dropdownText ? (
          <>
            {!Array.isArray(dropdownValue) && dropdownValue.icon && (
              <div className="flex w-4 min-w-[16px] flex-row items-center">
                <Icon name={dropdownValue.icon} color={brandTintColor} />
              </div>
            )}
            <Text
              as="span"
              variant={
                variant === "largeSearch"
                  ? "h5_t-18-700"
                  : buttonTextSizeClasses[buttonTextSize].text
              }
              color={brandTintColor}
              className={classNames(
                "pointer-events-none block w-full truncate text-left",
                !isBrandTint && "text-straps-primary"
              )}
            >
              {dropdownText}
            </Text>
          </>
        ) : (
          <span
            className={classNames("whitespace-nowrap", {
              "text-lg font-bold text-white/90": isBrandTint,
              "text-straps-tertiary": !isBrandTint,
              [buttonTextSizeClasses[buttonTextSize].tw]: !isBrandTint,
            })}
          >
            {placeholder}
          </span>
        )}
      </div>
      <div className="flex w-3 min-w-[12px] flex-row items-center">
        <Icon
          name="caret-down"
          size="small"
          color={brandTintColor}
          className={classNames("transition-all", {
            "hover:text-straps-primary group-hover:text-straps-primary group-focus:text-straps-primary":
              !isBrandTint,
            "-rotate-180 text-straps-primary": menuOpened && !isBrandTint,
            "text-straps-secondary": !menuOpened && !isBrandTint,
            "-rotate-180": menuOpened && isBrandTint,
          })}
        />
      </div>
    </button>
  );
};

export default forwardRef(DropdownTarget);
